<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import {
  AtomText,
  MolButtonRound,
  SatIconButton,
} from "@chatfood/design-system";
import { computed } from "vue";
import { LoyaltyRewards } from "../loyalty-rewards";
import type { ITheMobileHeaderProps } from ".";
import { useNavigation } from "~/composables/use-navigation";
import { useCustomerStore } from "~/stores/customer";
import { useBasketStore } from "~/stores/basket";
import { useLoyaltyProgramStore } from "~/stores/loyaltyProgram";

const css = bemBuilder("the-mobile-header");

const props = withDefaults(defineProps<ITheMobileHeaderProps>(), {
  noShadow: false,
  hideCta: false,
  hideActions: false,
});

const { resolvePath, back } = useNavigation();

const basket = useBasketStore();
const loyaltyProgram = useLoyaltyProgramStore();
const customer = useCustomerStore();

const onCtaClick = () => {
  if (props.ctaAction) {
    props.ctaAction();
    return;
  }

  back();
};

const displayLoyaltyRewards = computed(() => {
  return (
    !props.hideActions &&
    loyaltyProgram.isEnabled &&
    loyaltyProgram.service === "deliverect" &&
    loyaltyProgram.canRedeemPoints &&
    customer.isLogged &&
    basket.isLoaded
  );
});

const displayProfileButton = computed(() => {
  return !props.hideActions && customer.isLogged;
});
</script>

<template>
  <header
    :class="{
      [css()]: true,
      [css('', 'no-shadow')]: props.noShadow,
    }"
  >
    <SatIconButton
      v-if="!props.hideCta"
      :icon-name="props.ctaIcon ?? 'back'"
      variant="secondary"
      @click="onCtaClick"
    />
    <AtomText type="subtitle" color="color-text-headline" :class="css('title')">
      {{ props.title }}
    </AtomText>
    <LoyaltyRewards v-if="displayLoyaltyRewards" />
    <MolButtonRound
      v-if="displayProfileButton"
      shadow
      :icon-name="'user-circle'"
      :to="resolvePath('my-account', {})"
    />
  </header>
</template>

<style lang="scss" scoped>
.the-mobile-header {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  justify-content: space-between;
  box-shadow: var(--elevation-level-1);
  padding: var(--spacing-md);
  z-index: 1;

  &--no-shadow {
    box-shadow: none;
  }

  &__title {
    width: 100%;
    text-align: start;
  }

  @media (width > $breakpoint-sm) {
    display: none;
  }
}
</style>
